// Phase 43-09 — <CanvasListingDescriptionSection /> (CANVAS-01 gap closure).
// Renders the long-form 'description' editor. The streaming preview UI from
// the wizard's AIDescriptionCard is OUT OF SCOPE for gap closure.
//
// #607 — the Regenerate button this file originally shipped has been REMOVED
// (see the comment at its old site below): the canvas has no apply path for a
// streamed description, so the button did nothing. Restore it together with
// the canvas surface migration.
//
// #787 — the plain Textarea is replaced by the restricted SyncfusionEditor
// (only formatting the server sanitizer, #786, preserves). The field moves
// from register() to a controlled Controller because the editor emits HTML
// strings via onChange rather than DOM input events.
//
// D-15 (V1 era): the wizard folder was never modified by the canvas work. Type-only imports of
// WizardFormData and useWizardAI return type are allowed.
//
// Typography lock (UI-SPEC rev 2): 13px label font-normal, 14px body
// font-normal. The 12px size and 500-weight Tailwind utility class names
// are disallowed by UI-SPEC and appear nowhere in this file.

import { Controller, useFormContext } from 'react-hook-form';
import { Label } from '@marine/ui/label';
import { SyncfusionEditor } from '@/components/editor/SyncfusionEditor';
import type { useWizardAI } from '@/modules/inventory/components/boat-form/hooks/useWizardAI';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';

interface CanvasListingDescriptionSectionProps {
  wizardAI: ReturnType<typeof useWizardAI> | undefined;
}

// #607 — `wizardAI` is intentionally still on the props interface but no longer
// destructured: the Regenerate button was its only consumer. The prop stays so
// the parent does not have to change, and so the canvas surface migration can
// restore the button without re-threading it.
export function CanvasListingDescriptionSection(
  _props: CanvasListingDescriptionSectionProps,
) {
  const { control } = useFormContext<WizardFormData>();
  const inputId = 'canvas-field-description';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label
          htmlFor={inputId}
          className="text-[0.8125rem] font-normal"
        >
          Description
        </Label>
        {/* #607 — Regenerate removed: the canvas has no apply path for a streamed
            description, so the button did nothing. Restore it together with the
            canvas surface migration. */}
      </div>
      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <SyncfusionEditor
            id={inputId}
            value={field.value ?? ''}
            onChange={field.onChange}
            preset="restricted"
            ariaLabel="Description"
            className="text-sm font-normal"
            placeholder="Long-form listing description"
            height={300}
          />
        )}
      />
    </div>
  );
}
